/**
 * Raid Items Panel
 *
 * Compact floating panel for item usage during raid draft phase.
 * Click/drag item to target pokemon (roster cards or PIXI sprites).
 * Modal stays open after using items for quick consecutive usage.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import classNames from "classnames";
import { RaidPokemon, RAID_ITEM_EFFECTS, RaidUsableItem } from "types/raid-dynamodb";
import { UsableItem } from "@/providers/raid-provider";

// ============================================================================
// Types
// ============================================================================

export interface ItemTargetingState {
  selectedItem: RaidUsableItem | null;
  validTargetIds: number[];
  isDragging: boolean;
  dragPosition: { x: number; y: number } | null;
}

interface RaidItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: UsableItem[];
  allPokemon: RaidPokemon[];
  faintedPokemonIds: number[];
  pokemonHp: Record<number, number>;
  onUseItem: (itemName: string, targetPokemonId: number) => void;
  /** Callback when targeting state changes (for VFX on roster/PIXI) */
  onTargetingChange?: (state: ItemTargetingState) => void;
}

// ============================================================================
// Item Display Config (compact - no descriptions in UI)
// ============================================================================

const ITEM_DISPLAY: Record<RaidUsableItem, { label: string; color: string }> = {
  potion: { label: "Potion", color: "bg-pk-green-3" },
  "super-potion": { label: "Super", color: "bg-pk-green-4" },
  "hyper-potion": { label: "Hyper", color: "bg-pk-blue-3" },
  "max-potion": { label: "Max", color: "bg-pk-blue-4" },
  revive: { label: "Revive", color: "bg-pk-yellow-3" },
  "max-revive": { label: "Max Rev", color: "bg-pk-orange-3" },
};

// ============================================================================
// Main Component
// ============================================================================

export const RaidItemsModal = ({
  isOpen,
  onClose,
  items,
  allPokemon,
  faintedPokemonIds,
  onUseItem,
  onTargetingChange,
}: RaidItemsModalProps) => {
  const [selectedItem, setSelectedItem] = useState<RaidUsableItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const dragTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get item effect type for filtering targets
  const selectedItemEffect = selectedItem
    ? RAID_ITEM_EFFECTS[selectedItem]
    : null;
  const isReviveItem = selectedItemEffect?.type === "revive";

  // Compute valid target IDs based on selected item
  const validTargetIds = useMemo(() => {
    if (!selectedItem) return [];

    return allPokemon
      .filter((pokemon) => {
        const isFainted = faintedPokemonIds.includes(pokemon.id);
        if (isReviveItem) return isFainted;
        return !isFainted;
      })
      .map((p) => p.id);
  }, [allPokemon, faintedPokemonIds, selectedItem, isReviveItem]);

  // Notify parent of targeting state changes
  useEffect(() => {
    onTargetingChange?.({
      selectedItem,
      validTargetIds,
      isDragging,
      dragPosition,
    });
  }, [selectedItem, validTargetIds, isDragging, dragPosition, onTargetingChange]);

  // Clear targeting state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedItem(null);
      setIsDragging(false);
      setDragPosition(null);
    }
  }, [isOpen]);

  // Handle item click (toggle selection)
  const handleItemClick = useCallback((itemName: RaidUsableItem) => {
    setSelectedItem((prev) => (prev === itemName ? null : itemName));
    setIsDragging(false);
    setDragPosition(null);
  }, []);

  // Handle drag start (long press or immediate drag)
  const handleDragStart = useCallback(
    (itemName: RaidUsableItem, clientX: number, clientY: number) => {
      setSelectedItem(itemName);
      setIsDragging(true);
      setDragPosition({ x: clientX, y: clientY });
    },
    [],
  );

  // Handle drag move
  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      if (isDragging) {
        setDragPosition({ x: clientX, y: clientY });
      }
    },
    [isDragging],
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDragPosition(null);
    // Note: actual item usage is triggered by drop target detection in parent
  }, []);

  // Global mouse/touch move handlers for drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => handleDragMove(e.clientX, e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const handleEnd = () => handleDragEnd();

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchend", handleEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    };
  }, []);

  // Reset and close
  const handleClose = useCallback(() => {
    setSelectedItem(null);
    setIsDragging(false);
    setDragPosition(null);
    onClose();
  }, [onClose]);

  // Public method to apply item to target (called by parent when target is clicked)
  const applyItemToTarget = useCallback(
    (pokemonId: number) => {
      if (!selectedItem) return false;
      if (!validTargetIds.includes(pokemonId)) return false;

      onUseItem(selectedItem, pokemonId);
      // Don't clear selection - allow consecutive usage
      return true;
    },
    [selectedItem, validTargetIds, onUseItem],
  );

  // Expose applyItemToTarget via ref-like pattern through onTargetingChange
  useEffect(() => {
    if (onTargetingChange) {
      // Attach method to window for cross-component access
      (window as any).__raidItemApply = applyItemToTarget;
    }
    return () => {
      delete (window as any).__raidItemApply;
    };
  }, [applyItemToTarget, onTargetingChange]);

  if (!isOpen) return null;

  return (
    <>
      {/* Modal */}
      <div
        className={classNames(
          "fixed top-14 left-1/2 -translate-x-1/2 z-[200]",
          "bg-pk-gray-1 border-2 border-pk-gray-3 rounded-lg",
          "w-auto max-w-[280px] overflow-hidden",
          "shadow-xl pointer-events-auto",
          "animate-in slide-in-from-top-2 fade-in duration-150",
        )}
      >
        {/* Compact Header */}
        <div className="bg-pk-gray-2 px-2 py-1.5 flex items-center justify-between border-b border-pk-gray-3">
          <span className="text-pk-gray-5 text-xs font-bold uppercase tracking-wide">
            Items
          </span>
          <button
            onClick={handleClose}
            className="text-pk-gray-4 hover:text-white transition p-0.5 -mr-0.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Compact Item Grid */}
        <div className="p-1.5">
          <CompactItemGrid
            items={items}
            selectedItem={selectedItem}
            onItemClick={handleItemClick}
            onDragStart={handleDragStart}
          />
        </div>

        {/* Targeting hint */}
        {selectedItem && !isDragging && (
          <div className="px-2 pb-1.5 text-center">
            <span className="text-pk-gray-4 text-[10px]">
              Tap pokemon to use • Drag for precision
            </span>
          </div>
        )}
      </div>

      {/* Dragging item cursor */}
      {isDragging && dragPosition && selectedItem && (
        <div
          className="fixed z-[300] pointer-events-none"
          style={{
            left: dragPosition.x - 20,
            top: dragPosition.y - 20,
          }}
        >
          <div
            className={classNames(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              "border-2 border-white shadow-lg",
              ITEM_DISPLAY[selectedItem]?.color || "bg-pk-gray-3",
            )}
          >
            <img
              src={`/assets/items/${selectedItem}.png`}
              alt=""
              className="w-8 h-8 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// Compact Item Grid
// ============================================================================

interface CompactItemGridProps {
  items: UsableItem[];
  selectedItem: RaidUsableItem | null;
  onItemClick: (itemName: RaidUsableItem) => void;
  onDragStart: (itemName: RaidUsableItem, clientX: number, clientY: number) => void;
}

const CompactItemGrid = ({
  items,
  selectedItem,
  onItemClick,
  onDragStart,
}: CompactItemGridProps) => {
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingRef = useRef(false);

  const handlePointerDown = useCallback(
    (item: RaidUsableItem, e: React.PointerEvent) => {
      isDraggingRef.current = false;
      const startX = e.clientX;
      const startY = e.clientY;

      // Start long press timer for drag
      longPressRef.current = setTimeout(() => {
        isDraggingRef.current = true;
        onDragStart(item, startX, startY);
      }, 200);
    },
    [onDragStart],
  );

  const handlePointerUp = useCallback(
    (item: RaidUsableItem) => {
      if (longPressRef.current) {
        clearTimeout(longPressRef.current);
        longPressRef.current = null;
      }
      // Only trigger click if we weren't dragging
      if (!isDraggingRef.current) {
        onItemClick(item);
      }
      isDraggingRef.current = false;
    },
    [onItemClick],
  );

  const handlePointerLeave = useCallback(() => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  }, []);

  if (items.length === 0) {
    return (
      <div className="text-center py-4 text-pk-gray-5 text-xs">
        No items available
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-1">
      {items.map((item) => {
        const display = ITEM_DISPLAY[item.item_name];
        if (!display) return null;

        const isSelected = selectedItem === item.item_name;

        return (
          <button
            key={item.item_name}
            onPointerDown={(e) => handlePointerDown(item.item_name, e)}
            onPointerUp={() => handlePointerUp(item.item_name)}
            onPointerLeave={handlePointerLeave}
            className={classNames(
              "relative flex flex-col items-center p-1.5 rounded-md transition-all",
              "touch-none select-none",
              {
                "bg-pk-blue-3/30 ring-2 ring-pk-blue-4": isSelected,
                "bg-pk-gray-2 hover:bg-pk-gray-3": !isSelected,
              },
            )}
          >
            {/* Item Icon */}
            <div
              className={classNames(
                "w-9 h-9 rounded-md flex items-center justify-center",
                display.color,
              )}
            >
              <img
                src={`/assets/items/${item.item_name}.png`}
                alt={display.label}
                className="w-7 h-7 object-contain"
                style={{ imageRendering: "pixelated" }}
                draggable={false}
              />
            </div>

            {/* Quantity Badge */}
            <div className="absolute -top-0.5 -right-0.5 bg-pk-gray-1 border border-pk-gray-3 px-1 rounded text-[9px] text-white font-bold">
              {item.amount}
            </div>

            {/* Label */}
            <span className="text-[10px] text-pk-gray-5 mt-0.5 leading-tight text-center">
              {display.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

